<template>
    <pc-capsule
        v-bind="capsuleBinderOverride"
        v-bind:shown="expanded"
        has-body
        v-on="capsuleListeners">
        <div>
            <div
                v-bind:class="responsiveClasses.statusDisplayWrapper"
                class="row">
                <div v-bind:class="responsiveClasses.statusDisplayRow1">
                    <div
                        id="StatusDisplayText"
                        class="status-row">
                        <span>{{ statusDisplayText }}</span>
                        <strong>{{ statusName }}</strong>
                        <span>{{ statusTimestamp }}</span>
                        <span>{{ changedByInfo }}</span>
                    </div>
                    <div v-if="statusCaption">
                        {{ statusCaption }}
                    </div>
                </div>
                <div
                    v-if="!isAdminPage"
                    v-bind:class="responsiveClasses.statusDisplayRow2">
                    <hr v-bind:class="responsiveClasses.statusDisplayHr" />
                    <div class="d-flex align-items-center">
                        <div>{{ projectOwnerText }}</div>
                        <div
                            id="owner-name"
                            class="fw-bold ms-1">
                            {{ projectOwnerInfo }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!isAdminPage">
                <hr v-bind:class="responsiveClasses.statusDisplayHr" />
                <div>{{ createdTimestamp }}</div>
                <div>{{ updatedTimestamp }}</div>
            </div>
            <div
                v-if="!showStatusControls && (showStatusChangeLink || showStatusHistoryLink)"
                class="row">
                <hr v-bind:class="responsiveClasses.statusDisplayHr" />
                <div
                    v-if="showStatusChangeLink"
                    v-bind:class="responsiveClasses.changeStatusLink">
                    <pc-button
                        v-if="!isReadOnlyUser"
                        id="ChangeStatusLink"
                        link
                        class="p-0"
                        v-on:click="showChangeStatusControls">
                        {{ changeStatusLink }}
                    </pc-button>
                </div>
                <div
                    v-if="showStatusHistoryLink"
                    v-bind:class="responsiveClasses.statusHistoryLink"
                    class="d-flex align-items-center">
                    <a
                        id="ViewStatusHistoryLink"
                        v-bind:href="statusHistoryUrl"
                        target="_blank">
                        {{ viewStatusHistoryLink }}
                    </a>
                </div>
            </div>
        </div>
        <div v-if="showStatusControls">
            <div class="row align-items-end">
                <div v-bind:class="responsiveClasses.changeStatusSelect">
                    <pc-select
                        id="statusList"
                        v-model="workflowStatusIdTo"
                        v-bind:optgroups="availableStatusOptgroups"
                        v-bind:options="availableStatusOptions"
                        text-prop="Name"
                        value-prop="PublicId"
                        label="Change status to:">
                        <option
                            v-if="checkAvailableStatuses"
                            value="">
                            {{ statusDropdownInstructionText }}
                        </option>
                        <option
                            v-else
                            value="">
                            {{ noAvailableStatusesText }}
                        </option>
                    </pc-select>
                </div>
                <div
                    v-bind:class="responsiveClasses.changeStatusButtons"
                    class="d-flex align-items-end justify-content-end">
                    <pc-button
                        id="BtnCancel"
                        class="me-2"
                        v-bind:disabled="changePending"
                        v-on:click="cancelStatusChange">
                        {{ cancelText }}
                    </pc-button>
                    <pc-button
                        id="BtnSave"
                        primary
                        v-bind:disabled="!canSaveNewStatus || !checkAvailableStatuses || changePending"
                        v-on:click="changeStatus">
                        {{ saveText }}
                    </pc-button>
                </div>
            </div>
            <pc-transition>
                <div
                    v-if="showOutsideWorkflowWarning"
                    class="mt-3 mb-0 d-block alert alert-info">
                    {{ outsideWorkflowWarning }}
                </div>
            </pc-transition>
            <pc-transition>
                <div
                    v-if="showPreviewInfo"
                    v-bind:class="previewClass"
                    class="mt-3 mb-0">
                    <automation-preview v-bind:automation-preview-models="automationPreviewModels" />
                    <div v-if="!hasValidationErrors && hasCommActions">
                        <pc-select
                            id="autoCommChoices"
                            v-model="autoCommunicationActionId"
                            v-bind:options="autoCommunicationActionOptions"
                            label="On status change:" />
                    </div>
                </div>
            </pc-transition>
        </div>
    </pc-capsule>
</template>
<script>
    import Vuex from "vuex";
    import capsuleBase, { WIDE_BP } from "./_sharedCapsuleBase";
    import { formatBasicUserInfoForDisplay } from "Util/clientUser";
    import automationPreview from "Automations/preview/automationPreview.vue";
    import { isModuleRegistered } from "SharedStores/util";
    import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import PcButton from "Components/pc-button.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/currentStatusCapsule.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { getResponsiveClassMap } from "Responsive/manager";

    const autoCommunicationBehaviors = {
        Send: 0,
        Save: 1,
        DoNothing: 2
    };

    const responsiveConfig = {
        statusDisplayWrapper: {
            mb: { xs: 0, [WIDE_BP.name]: 3 }
        },
        statusDisplayRow1: {
            col: { xs: 12, [WIDE_BP.name]: "" }
        },
        statusDisplayHr: {
            d: { xs: "block", [WIDE_BP.name]: "none" },
            always: "pc-capsule-hr"
        },
        statusDisplayRow2: {
            col: { xs: 12, [WIDE_BP.name]: 6 }
        },
        changeStatusLink: {
            col: { xs: 12, [WIDE_BP.name]: "" }
        },
        statusHistoryLink: {
            col: { xs: 12, [WIDE_BP.name]: 6 },
            "justify-content": { xs: "start", [WIDE_BP.name]: "end" }
        },
        changeStatusSelect: {
            col: { xs: 12, [WIDE_BP.name]: 6 },
            mb: { xs: 2, [WIDE_BP.name]: 0 }
        },
        changeStatusButtons: {
            col: { xs: 12, [WIDE_BP.name]: "" }
        }
    };

    export default {
        components: {
            automationPreview,
            PcButton
        },
        extends: capsuleBase,
        props: {
            id: { type: String, required: false, default: "current-status" },
            model: { type: Object, required: true },
            hasAutomations: { type: Boolean, required: false, default: true }, // automations are always enabled
            projectOwner: { type: Object, required: false, default: null }, // non-admin only
            projectOwnerRole: { type: Object, required: false, default: null } // non-admin only
        },
        data() {
            return {
                showStatusControls: false,
                availableStatuses: null,
                changePending: false,
                workflowStatusIdTo: null,
                statusTransitionPreview: null,
                autoCommunicationActions: null,
                autoCommunicationActionId: autoCommunicationBehaviors.Send,
                autoCommunicationActionOptions: [
                    { value: autoCommunicationBehaviors.Send, text: "Send and save communication" },
                    { value: autoCommunicationBehaviors.Save, text: "Save communication as draft" },
                    { value: autoCommunicationBehaviors.DoNothing, text: "Do not send or save communication" }
                ]
            };
        },
        computed: {
            ...mapI18n(KEYS, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE),
            capsuleBinderOverride() {
                return {
                    ...this.capsuleBinder,
                    title: this.currentStatusLabel
                };
            },
            checkAvailableStatuses() {
                if (this.availableStatuses) {
                    return (
                        this.availableStatuses.InWorkflow.length > 0 ||
                        this.availableStatuses.OutsideWorkflow.length > 0
                    );
                }
                return false;
            },
            projectOwnerInfo() {
                return formatBasicUserInfoForDisplay(this.projectOwner, this.projectOwnerRole);
            },
            statusDisplayInfo() {
                return (this.model && this.model.StatusDisplayInfo) || {};
            },
            statusName() {
                return this.statusDisplayInfo.StatusName;
            },
            statusTimestamp() {
                if (!this.statusDisplayInfo.StatusTimestamp) {
                    return "";
                }
                return `${this.onStatusText} ${this.statusDisplayInfo.StatusTimestamp.Display}`;
            },
            changedByInfo() {
                return this.statusDisplayInfo.ChangedByUserInfo != null
                    ? `${this.byStatusText} ${this.statusDisplayInfo.ChangedByUserInfo}`
                    : null;
            },
            statusCaption() {
                return this.model && this.model.StatusCaption;
            },
            createdTimestamp() {
                return this.model && `${this.createdOnText} ${this.model.CreatedTimestamp}`;
            },
            updatedTimestamp() {
                return this.model && `${this.updatedOnText} ${this.model.UpdatedTimestamp}`;
            },
            statusHistoryUrl() {
                return this._getChildPageUrl(this.model.StatusHistoryUrl);
            },
            availableStatusOptgroups() {
                if (!this.availableStatuses) {
                    return null;
                }

                if (!this.hasOutsideWorkflowOptions) {
                    return null;
                }

                return [
                    {
                        name: this.availableInWorkflowText,
                        options: this.availableStatuses.InWorkflow
                    },
                    {
                        name: this.outsideNormalWorkflowText,
                        options: this.availableStatuses.OutsideWorkflow
                    }
                ];
            },
            availableStatusOptions() {
                if (!this.availableStatuses) {
                    return null;
                }
                return !this.hasOutsideWorkflowOptions ? this.availableStatuses.InWorkflow : null;
            },
            canSaveNewStatus() {
                return Boolean(this.workflowStatusIdTo) && !this.hasValidationErrors;
            },
            hasOutsideWorkflowOptions() {
                return this.availableStatuses && this.availableStatuses.OutsideWorkflow.length > 0;
            },
            hasTransitionPreview() {
                return this.statusTransitionPreview != null;
            },
            hasValidationErrors() {
                return (
                    this.hasTransitionPreview &&
                    (this.statusTransitionPreview.Exit.FailedValidations.length > 0 ||
                        this.statusTransitionPreview.Transition.FailedValidations.length > 0 ||
                        this.statusTransitionPreview.Enter.FailedValidations.length > 0)
                );
            },
            hasPotentialActions() {
                return (
                    this.hasTransitionPreview &&
                    (this.statusTransitionPreview.Exit.PotentialActions.length > 0 ||
                        this.statusTransitionPreview.Transition.PotentialActions.length > 0 ||
                        this.statusTransitionPreview.Enter.PotentialActions.length > 0)
                );
            },
            automationPreviewModels() {
                return [
                    {
                        triggerDescription: this.exitingCurrentStatusMessage,
                        failedValidations: this.statusTransitionPreview.Exit.FailedValidations,
                        potentialActions: this.statusTransitionPreview.Exit.PotentialActions
                    },
                    {
                        triggerDescription: this.statusTransitionMessage,
                        failedValidations: this.statusTransitionPreview.Transition.FailedValidations,
                        potentialActions: this.statusTransitionPreview.Transition.PotentialActions
                    },
                    {
                        triggerDescription: this.enteringNewStatusMessage,
                        failedValidations: this.statusTransitionPreview.Enter.FailedValidations,
                        potentialActions: this.statusTransitionPreview.Enter.PotentialActions
                    }
                ];
            },
            showOutsideWorkflowWarning() {
                return this.availableStatuses.OutsideWorkflow.some((s) => s.PublicId === this.workflowStatusIdTo);
            },
            hasCommActions() {
                return (
                    this.hasTransitionPreview &&
                    (this.statusTransitionPreview.Exit.HasCommunicationActions ||
                        this.statusTransitionPreview.Transition.HasCommunicationActions ||
                        this.statusTransitionPreview.Enter.HasCommunicationActions)
                );
            },
            previewClass() {
                return this.hasValidationErrors ? "alert alert-danger" : "alert alert-info";
            },
            showPreviewInfo() {
                return (
                    this.hasCommActions ||
                    (this.hasTransitionPreview && (this.hasPotentialActions || this.hasValidationErrors))
                );
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            controller() {
                return this.$serviceControllers.CurrentStatus;
            },
            showStatusChangeLink() {
                return this.isAdminPage && this.$app.LoggedInRole.RolePrivileges.ProjectAdminCanChangeStatus;
            },
            showStatusHistoryLink() {
                return this.isAdminPage && this.$app.LoggedInRole.RolePrivileges.ProjectAdminCanViewStatusHistory;
            }
        },
        watch: {
            workflowStatusIdTo() {
                this.previewStatusTransition();
            }
        },
        methods: {
            async showChangeStatusControls() {
                if (!this.availableStatuses) {
                    await this.getAvailableStatuses();
                }
                this.showStatusControls = true;
            },
            cancelStatusChange() {
                if (this.changePending) {
                    return;
                }

                this.statusTransitionPreview = null;
                this.autoCommunicationActions = null;
                this.showStatusControls = false;
            },
            async getAvailableStatuses() {
                const result = await this.controller.GetAvailableStatuses({
                    programId: this.ProgramId,
                    projectId: this.ProjectId
                });
                if (result.status === "success") {
                    this.availableStatuses = result.Data;
                }
            },
            async previewStatusTransition() {
                const result = await this.controller.PreviewStatusTransition({
                    programId: this.ProgramId,
                    projectId: this.ProjectId,
                    workflowStatusIdTo: this.workflowStatusIdTo,
                    currentStatusId: this.model.CurrentStatus.PublicId
                });
                if (result.status === "success") {
                    this.statusTransitionPreview = result.Data;
                }
            },
            async changeStatus() {
                if (this.changePending) {
                    return;
                }

                if (isModuleRegistered(PROJECT_FORM_NAMESPACE, this.$store.state)) {
                    await this.confirmDiscardUnsavedData();
                }

                this.changePending = true;
                this.$app.blockUi(this.statusUpdateInProgressText);
                try {
                    await this.controller.ChangeStatus({
                        programId: this.ProgramId,
                        projectId: this.ProjectId,
                        workflowStatusIdTo: this.workflowStatusIdTo,
                        autoCommActionId: this.autoCommunicationActionId
                    });

                    this.$app.reloadWindow();
                } catch (e) {
                    // Since a status change is going to reload the page once complete,
                    // only unblock it if there's an unexpected issue
                    this.$app.unBlockUi();
                    throw e;
                } finally {
                    this.changePending = false;
                }
            },
            ...Vuex.mapActions({
                confirmDiscardUnsavedData: PROJECT_FORM_TYPES.actions.confirmDiscardUnsavedData
            })
        }
    };
</script>
<style lang="scss" scoped>
    .indent-panel {
        margin-left: 1.5rem;
    }
</style>
